/**
 * The shapes core-api returns. Hand-written rather than generated: unlike the event contract in
 * `src/contracts/` (which is vendored from the `shared-contracts` repo and must never be
 * edited), these are this service's own REST surface, and there is no generator wired for it.
 * Keep in step with `services/core-api/src/schemas.py` in the platform repo.
 */

export const ROLES = ["superadmin", "admin", "supervisor", "rep"] as const;
export type Role = (typeof ROLES)[number];

/** Widest first — index order is the hierarchy, mirroring `rbac.py`. */
const RANK: Record<Role, number> = { rep: 0, supervisor: 1, admin: 2, superadmin: 3 };

export function atLeast(role: Role | undefined, minimum: Role): boolean {
  return role !== undefined && RANK[role] >= RANK[minimum];
}

/** Which roles this actor may hand out. Mirrors `rbac.can_assign_role`; the server enforces it
 *  regardless, so this exists to keep impossible options out of the picker rather than to
 *  secure anything. */
export function assignableRoles(actor: Role | undefined): Role[] {
  if (!actor) return [];
  if (actor === "superadmin") return [...ROLES];
  return ROLES.filter((r) => RANK[actor] > RANK[r]);
}

/** Whether this person changes their own phone — its extension and whether it records — from
 *  their account settings. Admins and superadmins only: below admin, an admin sets both on the
 *  person's user record. Mirrors the role floor on core-api's `PUT /auth/me/endpoint`, which is
 *  what actually refuses it. */
export function editsOwnPhone(role: Role | undefined): boolean {
  return atLeast(role, "admin");
}

export const ROLE_LABELS: Record<Role, string> = {
  superadmin: "Superadmin",
  admin: "Admin",
  supervisor: "Supervisor",
  rep: "Rep",
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  superadmin: "Every tenant. Manages tenants, domains, and all users and endpoints.",
  admin: "One tenant, every domain in it. Manages its people and the phones that come with them.",
  supervisor: "One domain. Reads its people and their live state; cannot manage identity.",
  rep: "Themselves and their phone.",
};

/** The admin who opened this session as somebody else, on `/auth/me` (ADR-040 in `platform`). */
export interface SignedInBy {
  id: number;
  email: string;
  display_name: string | null;
}

export interface AppUser {
  id: number;
  email: string;
  display_name: string | null;
  role: Role;
  is_active: boolean;
  tenant_id: number | null;
  tenant_name: string | null;
  domain_id: number | null;
  domain_name: string | null;
  sip_endpoint_id: number | null;
  sip_endpoint_extension: string | null;
  sip_endpoint_domain_name: string | null;
  /** Every identifier the endpoint answers to: its auth id, and the `number-alias` it dials as
   *  when one is set. `sip_endpoint_extension` is the one to show; this is the set to match a
   *  call leg against (`counterparty` in `lib/calls.ts`). Empty when there is no phone. */
  sip_endpoint_numbers: string[];
  /** Whether their phone records its own leg. `null` means they have no phone — distinct from
   *  `false`, which is a phone deliberately opted out. */
  sip_endpoint_record_calls: boolean | null;
  /** Whether the endpoint is in the switch's directory at all. A disabled one cannot register,
   *  so the console shows the phone as unavailable rather than merely signed out. */
  sip_endpoint_enabled: boolean | null;
  last_login_at: string | null;
  created_at: string;
  /** Only on `/auth/me`: set when an admin opened this session with a sign-in link. */
  signed_in_by?: SignedInBy | null;
}

/** Exactly four digits — the rule `core-api`'s `provisioning.py` enforces for a person's
 *  extension. Deliberately narrower than what an endpoint nobody owns may be called: a device's
 *  id is not a number anyone dials. */
export const EXTENSION_RE = /^\d{4}$/;

export interface Tenant {
  id: number;
  name: string;
  slug: string;
  status: "active" | "suspended";
  created_at: string;
  updated_at: string;
  user_count: number;
}

export interface Domain {
  id: number;
  name: string;
  json_data: Record<string, unknown>;
  outbound_routing: OutboundRouting;
  created_at: string;
  endpoint_count: number;
  /** The trunks `outbound_routing` resolves to right now, in dial order — what the switch would
   *  actually use. Empty means no call can leave this domain: either nothing is configured at
   *  all, or every trunk the domain chose has since been disabled. */
  outbound_gateways: GatewaySummary[];
  /** Whether a recording made in this domain is turned into text when it lands. The firm's call,
   *  not the rep's: `record_calls` on a phone decides whether there is audio at all, and this
   *  only decides what happens to it. Turning it off stops new jobs and keeps the transcripts
   *  already written. */
  transcribe_recordings: boolean;
}

/**
 * Which trunks a domain's reps dial out on. Mirrors
 * `services/shared/telephony/outbound.py` — the module both core-api and the XML gateway read
 * this shape with.
 *
 * `auto` is the default: the first enabled trunk on the external profile, which is what every
 * domain did before this was configurable. `gateways` is the operator's own ordered list, tried
 * left to right, and it is strict — if every trunk in it is disabled or gone, calls are
 * announced and dropped rather than sent out on a carrier the domain did not choose.
 */
export const OUTBOUND_MODES = ["auto", "gateways"] as const;
export type OutboundMode = (typeof OUTBOUND_MODES)[number];

export interface OutboundRouting {
  mode: OutboundMode;
  /** Gateway ids, in failover order. Empty in `auto` mode. */
  gateway_ids: number[];
  /**
   * What a rep with no DID of their own presents on a PSTN call, or null for no default.
   *
   * Only the *fallback*: a rep with a number routed to their phone or their room calls out as
   * that number, decided by the switch's XML gateway rather than here (ADR-013). Kept in both
   * modes — which trunk carries the call and which number it presents are independent choices.
   */
  caller_id_number: string | null;
}

/** A trunk as it appears inside a domain's resolved outbound list. */
export interface GatewaySummary {
  id: number;
  uuid: string;
  name: string;
  enabled: boolean;
}

/**
 * A SIP trunk — the carrier a call leaves on. Never carries the carrier password: reading that
 * back is its own request, the same as an endpoint's SIP password.
 */
export interface Gateway {
  id: number;
  /** What FreeSWITCH knows the trunk by; it appears in dial strings and CDRs and never changes.
   *  `name` is the label people read. */
  uuid: string;
  name: string;
  description: string | null;
  username: string;
  realm: string;
  proxy: string;
  register: boolean;
  profile_name: string;
  /** Extra sofia gateway params (`caller-id-in-from`, `expire-seconds`, …). */
  params: Record<string, string>;
  enabled: boolean;
  created_at: string;
  /** Domains routing outbound calls through this trunk. Non-zero blocks deleting it. */
  domain_count: number;
}

export interface GatewaySecret {
  name: string;
  username: string;
  password: string;
}

export interface Endpoint {
  id: number;
  extension: string;
  domain_id: number;
  domain_name: string | null;
  display_name: string | null;
  caller_id_number: string | null;
  number_alias: string | null;
  enabled: boolean;
  /** Whether calls this phone places, or is rung for, are recorded. On by default. */
  record_calls: boolean;
  created_at: string;
  assigned_user_id: number | null;
  assigned_user_email: string | null;
  assigned_user_name: string | null;
  /** `null` means the live-state store was unreachable — "unknown", not "offline". */
  registered: boolean | null;
}

/**
 * One recorded call leg.
 *
 * `call_id` is the channel uuid — the same id a `Cdr` carries — which is how a recording is
 * matched to a row of call history without the calls API knowing recordings exist.
 *
 * `audio_url` is a path on core-api, not a bucket link, and it needs the session: the player
 * fetches it through the API client rather than putting it in an `<audio src>`, because an
 * `<audio>` element sends no Authorization header.
 */
export interface Recording {
  call_id: string;
  domain: string | null;
  endpoint_user: string | null;
  audio_url: string;
  mime: string;
  size_bytes: number;
  duration_ms: number | null;
  sample_rate: number | null;
  channels: number | null;
  fs_host: string | null;
  recorded_at: string | null;
  expires_at: string | null;
  direction: string | null;
  caller_number: string | null;
  caller_name: string | null;
  callee_number: string | null;
  callee_name: string | null;
  cdr_duration_seconds: number | null;
  /** The whole-file transcription job for this file, or null when none was asked for. */
  transcript_status: TranscriptStatus | null;
  /** One line about the file when the job failed. */
  transcript_error: string | null;
}

/**
 * Where the whole-file pass over a call's recording stands. `pending` covers queued and
 * decoding alike — the console has nothing different to say about the two. `failed` is about
 * the FILE (missing, undecodable, silence throughout), so asking again only helps once whatever
 * was wrong with it is not.
 */
export type TranscriptStatus = "pending" | "done" | "failed";

/** Which leg of the call a line's audio arrived on.
 *
 * Named for the CAPTURE and not for a role, because a rep-to-rep call has no answer to "which
 * one is the lead". `near` is the leg the recording or tap was taken on — FreeSWITCH writes that
 * channel's own read stream to channel 0, so the mapping is fixed — and `far` is the party it was
 * speaking to. `unknown` is an honest mono capture: one mixed track cannot be split by leg, and
 * guessing would be worse than saying so.
 */
export type TranscriptSpeaker = "near" | "far" | "unknown";

export interface TranscriptSegment {
  seq: number;
  text: string;
  /** Seconds from the start of the captured audio — so on a `recording` line it lines up with
   *  the recording's own playhead, which is what makes the line clickable. */
  start_seconds: number;
  end_seconds: number | null;
  speaker: TranscriptSpeaker;
}

/**
 * The stored transcript(s) of one call.
 *
 * **Two lists, never merged.** `live` is captured turn by turn during the call by the tap;
 * `recording` is a whole-file pass over the audio afterwards. They are independent passes over
 * the same conversation and can disagree — different segmentation, different words in the hard
 * places — and a reader who cannot tell which produced a line cannot judge it. Both may be
 * present, either may be empty.
 *
 * `recording_status` is the whole-file job's and says nothing about `live`: null means no pass
 * was asked for (the tenant does not transcribe, or there is no recording), which is not the same
 * as a pass that found nothing to say — that one is `failed`.
 */
export interface CallTranscript {
  call_id: string;
  live: TranscriptSegment[];
  recording: TranscriptSegment[];
  recording_status: TranscriptStatus | null;
  recording_error: string | null;
}

/** Which pass a stored transcript came from: the whole-file pass over the recording, or the
 *  tap while the call was on. */
export type TranscriptSource = "recording" | "live";

/** One of our phones on a call, and the person on it when there is one. */
export interface TranscriptRep {
  extension: string;
  name: string | null;
}

/**
 * One row of a contact's Transcripts tab (`GET /leads/{id}/transcripts`, ADR-081 in platform): one
 * pass over one of their calls. `id` + `source` name it, and are what `POST /transcripts/lines`
 * takes back — `id` is the uuid its lines are stored under, the recording's or the leg's, not a
 * CDR id.
 */
export interface TranscriptListItem {
  id: string;
  source: TranscriptSource;
  started_at: string | null;
  duration_seconds: number | null;
  /** Our phones on the call, in the order they joined it. */
  reps: TranscriptRep[];
  campaign_id: string | null;
  /** Null with a `campaign_id` when the campaign was deleted since. */
  campaign_name: string | null;
  line_count: number;
}

/** `POST /transcripts/lines`: the lines of one transcript, in the order they were spoken. */
export interface TranscriptLines {
  id: string;
  source: TranscriptSource;
  lines: TranscriptSegment[];
}

/** `POST /calls/{id}/transcribe`: the job is queued, not done. */
export interface TranscribeAccepted {
  call_id: string;
  status: "accepted";
}

export interface EndpointSecret {
  extension: string;
  domain_name: string;
  password: string;
}

export interface IceServer {
  urls: string | string[];
  username?: string | null;
  credential?: string | null;
}

/**
 * One person a rep can dial. Mirrors `ColleagueResponse`.
 *
 * Deliberately thin, and that is the server's decision showing through: `GET /phone/directory`
 * is the one directory route a **rep** may call, so it carries an extension, the name their
 * colleagues already see on an internal call, and whether the phone is online. No email, no
 * role, no domain — everyone on the list is in the caller's own realm by construction.
 */
export interface Colleague {
  extension: string;
  /** Null for an endpoint nobody holds — a shared handset, a door phone. Both are dialable. */
  name: string | null;
  /** `null` means the switch's live-state store was unreachable: "we don't know", which is not
   *  the same as offline and must not be shown as it. */
  registered: boolean | null;
}

/** `GET /phone/internal-transfer-numbers`: GoHighLevel's own numbers, each as its last ten digits. */
export interface InternalTransferNumbers {
  numbers: string[];
}

/**
 * One call the signed-in rep's phone did not take, as `GET /phone/missed` lists them (ADR-048 in
 * `platform`). Mirrors `MissedCallResponse`. Three records feed the list — a leg that rang the
 * phone, a stay the ACD kept for the rep, the caller's leg of a call on their direct number while
 * the phone was unregistered — and `id` is stable per record, so it is the list key.
 */
export interface MissedCall {
  id: string;
  /** When the call reached the rep, or was meant for them. */
  at: string;
  /** The caller's own channel where the platform knows it; on a plain leg, the leg's own uuid. */
  call_id: string | null;
  caller_number: string | null;
  caller_name: string | null;
  /** What happened on the rep's side: their phone rang and went unanswered; their phone was not
   *  registered; the ACD did not ring them (a break, signed out, on another call). */
  reason: "no_answer" | "offline" | "unavailable";
  /** How long their phone rang for this caller, every ring added up. Null when it never rang. */
  rang_seconds: number | null;
  /** The caller dialed the rep's own number: on a campaign (ADR-037) or a direct one (ADR-032). */
  own_number: boolean;
  did: string | null;
  queue_name: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  lead_id: string | null;
  /** What became of the caller, from the ACD's stay: CONNECTED, DROP, ABANDONED, TIMEOUT, REHOMED
   *  or REMOVED. Null for a call the ACD never saw, and for a stay still open. */
  outcome: string | null;
  /** Which way the call ran, where known: `inbound` for a caller, `outbound` for the dialer's
   *  call. An ABANDONED outcome is the caller giving up on one and the dialer on the other. */
  direction: string | null;
  /** The colleague who took the caller instead: their extension, and their name when known. */
  taken_by: string | null;
  taken_by_name: string | null;
}

export interface SoftphoneCredentials {
  ws_url: string;
  login: string;
  password: string;
  sip_endpoint_id: number;
  domain: string;
  extension: string;
  number_alias: string | null;
  caller_name: string;
  caller_number: string;
  ice_servers: IceServer[];
}

/**
 * What `POST /phone/room/participants` answers with. Mirrors `RoomParticipantResponse`.
 *
 * The room is not sent up — the server always uses the caller's own — so the answer is where the
 * console learns which room the caller was handed to.
 */
export interface RoomParticipant {
  room: string;
  profile: string;
  destination: string;
  /** The rep's own leg, echoed back: the caller moved is the one bridged to it. */
  call_id: string;
  command_id: string;
}

/**
 * What a conference roster row *is*. Mirrors `LegKind` in core-api's `src/legs.py`, which holds
 * the reasoning; the values are the wire format and the two have to agree.
 *
 * `unknown` is a leg the server found and could not place. The console renders no badge for it —
 * a row labelled "Unknown" tells a rep less than a row with nothing on it. `monitor` is a supervisor
 * listening in on a campaign call, whose row the phone drops (ADR-041).
 */
export type LegKind = "lead" | "caller" | "rep" | "room" | "monitor" | "unknown";

/** One identified leg. Mirrors `RoomLeg`. */
export interface RoomLeg {
  /** The roster's own hash key: mod_conference sets it to the member's channel uuid. */
  channel_id: string;
  kind: LegKind;
  /** Absent where direction says nothing about the participant, or the switch reported neither. */
  direction: "inbound" | "outbound" | null;
}

/**
 * What `POST /phone/room/legs` answers with. Mirrors `RoomLegsResponse`.
 *
 * Ids that are missing were not in the live-channel index — a leg that has already hung up, or
 * an index that could not be reached. Neither is an error: the row stays unlabelled.
 */
export interface RoomLegs {
  legs: RoomLeg[];
}

/**
 * A call on a rep's own number, in a room of its own (ADR-032). Mirrors `RoomCallResponse`.
 *
 * `joined` is false for a leg that is still ringing for somebody else's call, and then
 * `invited_by` / `invite_mode` say whose call it is and why. `caller_held` is what the switch
 * last reported about the outside party.
 */
export interface RoomMember {
  channel_id: string;
  member_id: number | null;
  kind: LegKind;
  direction: "inbound" | "outbound" | null;
  held: boolean;
  is_self: boolean;
  /** The colleague's extension, for one of the firm's legs. */
  extension: string | null;
  /** The outside party's number, for the caller's leg. */
  number: string | null;
  name: string | null;
  invited_by: string | null;
  invite_mode: InviteMode | null;
}

export type InviteMode = "consult" | "add";

export interface RoomInvite {
  channel_id: string;
  /** A colleague's extension, or null for an outside number (ADR-065), which is `number`. */
  extension: string | null;
  name: string | null;
  number?: string | null;
  /** Set for a short while after the leg hung up without joining: the switch's cause, so the
   *  phone can say why (ADR-065). Such an invite is no longer ringing. */
  ended_cause?: string | null;
  ended_at?: string | null;
  invited_by: string | null;
  mode: InviteMode | null;
  since: string | null;
}

export interface RoomCall {
  /** Null for a live leg on no room call: the ordinary answer for a call that just rings. */
  room: string | null;
  call_id: string;
  joined: boolean;
  caller_held: boolean;
  members: RoomMember[];
  invites: RoomInvite[];
  invited_by: string | null;
  invited_by_name: string | null;
  invite_mode: InviteMode | null;
}

/** What `POST /phone/room/hold` and `/phone/room/consult/end` answer with. */
export interface RoomCommands {
  room: string;
  call_id: string;
  command_ids: string[];
}

/** What `POST /phone/room/outside` answers with: the newcomer's leg, named before it rings. */
export interface OutsideParticipantResult {
  room: string;
  call_id: string;
  channel_id: string;
  number: string;
  name: string | null;
  via: "direct" | "acd";
  command_id: string;
}

/** An outside number a tenant's reps bring into calls by name (ADR-065). */
export interface SpeedDial {
  id: number;
  tenant_id: number;
  label: string;
  /** E.164. */
  number: string;
  sort_order: number;
}

/** What `POST /phone/room/invite` answers with: the colleague's leg, named before it rings. */
export interface RoomInviteResult {
  room: string;
  call_id: string;
  channel_id: string;
  extension: string;
  mode: InviteMode;
  command_id: string;
}

/** How a briefing ends (ADR-032): the colleague takes the caller, all three talk, or the rep who
 *  asked takes the caller back. */
export type ConsultOutcome = "complete" | "join" | "cancel";

/**
 * What `POST /phone/transfers` answers with. Mirrors `WarmTransferResponse`.
 *
 * Only the two ids the console already had. The far parties' channel uuids — the pair the server
 * actually bridged — are deliberately not in the answer: an unseen channel id is what authorises
 * a console to act on a call, and handing back two the browser never held would give it a handle
 * on calls that are no longer its own.
 */
export interface WarmTransfer {
  /** The rep's leg carrying the party who was handed over. */
  from_call_id: string;
  /** The rep's leg carrying the party who received them. */
  to_call_id: string;
  /** Correlates with `command.result.*` on the bus; useful in a support ticket. */
  command_id: string;
}

/**
 * What `POST /phone/transfers/blind` answers with. Mirrors `BlindTransferResponse`.
 *
 * `destination` comes back because the server normalises it — a number typed `(310) 555-0199`
 * is dialled as `3105550199` — and `context` because it is the one part of a blind transfer the
 * console does not choose: the rep's own domain, which is what decides whose `1003` was meant.
 */
export interface BlindTransfer {
  /** The rep's own leg, echoed back: the caller sent away is the one bridged to it. */
  call_id: string;
  destination: string;
  context: string;
  /** Correlates with `command.result.*` on the bus; useful in a support ticket. */
  command_id: string;
}

/** What `POST /phone/room` answers with. Mirrors `ConferenceRoomResponse` in `schemas.py`. */
export interface ConferenceRoom {
  /** `<extension>-room` — the room the user's phone was just rung into. */
  room: string;
  /** The mod_conference profile, served by fs-xml-gateway. */
  profile: string;
  /** `<room>@<profile>` — what anyone else dials or is transferred to, to reach it. */
  destination: string;
  extension: string;
  domain: string;
  /** Correlates with `command.result.*` on the bus; useful in a support ticket. */
  command_id: string;
}

// --- the ACD: taking calls ---------------------------------------------------------------------
//
// Mirrors `AgentStateResponse`, `AgentLine` and `AgentActionResponse` in core-api's `schemas.py`,
// and the live record the event-tracker writes (`AgentStateRecord` in
// `services/shared/state/queues.py`), which `/acd/me` passes through as it is — hence camelCase.

/** A line the rep staffs, and the campaign that owns it. */
export interface AgentLine {
  id: number;
  name: string;
  purpose: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  /** The line's campaign answers the calls it sends without a click. */
  force_auto_answer: boolean;
  /** The line's campaign lets its reps choose My calls only (ADR-037). */
  own_calls_only_allowed?: boolean;
  /** The rep's own numbers on the line's campaign (ADR-037): where the campaign's routing keeps them
   *  to their own callers. */
  own_numbers?: string[];
}

/** The event-tracker's record of a rep: what the ACD last did with them. */
export interface AgentStateRecord {
  user: string;
  domain: string;
  /** The effective state across every line: available, ringing, busy, disposition, wrapup,
   *  paused, dialing, busy_external or offline. */
  state: string;
  /** The lines the rep is signed in to. */
  queues: string[];
  queueStates: Record<string, string>;
  queue?: string | null;
  since?: string | null;
  /** The break reason, while paused. */
  code?: string | null;
  channelId?: string | null;
  callerChannelId?: string | null;
  sessionId?: string | null;
  leadId?: string | null;
  kind?: string | null;
  handoffId?: string | null;
  /** The other party on the call on hand, as the ACD knows them: the client on a line's call, or
   *  whoever is on a call of the rep's own. Kept while the rep rings, talks or dials. */
  callerNumber?: string | null;
  callerName?: string | null;
  /** A consult handoff being briefed right now (ADR-027): the handoff, the other rep's extension,
   *  and which side of it this rep is. Cleared when the briefing ends. */
  consultHandoffId?: string | null;
  consultWith?: string | null;
  consultRole?: string | null;
  /** The calls the rep finished in each line since signing in to it, as the ACD counts them. */
  callsHandledByQueue?: Record<string, number>;
  /** The conference room a call the ACD connected lives in (ADR-029), once the rep has taken it. */
  room?: string | null;
  /** The caller is on the hold a rep put them on in that room. */
  callerHeld?: boolean;
  /** The lines where the rep takes only the calls meant for them: My calls only (ADR-037), or
   *  for their next call (ADR-070). */
  ownCallsOnly?: string[];
  /** Of those, the lines where it lasts only until the rep answers a call meant for them: the
   *  choice they made for their next call (ADR-070). */
  ownCallsNext?: string[];
  /** The rep the call on hand was meant for (`user@domain`): the caller dialed that rep's own
   *  number, or a colleague sent them to that rep by name. */
  meantFor?: string | null;
}

/** `GET /acd/me`: the caller's own lines, skills and live state. */
export interface AgentState {
  user: string;
  domain: string;
  /** `null` when the live-state store could not say. */
  registered: boolean | null;
  lines: AgentLine[];
  skills: Record<string, number>;
  /** The campaign numbers that ring this rep first (ADR-037), E.164. */
  numbers?: string[];
  /** A line the rep is signed in to answers its calls without a click. Which calls the phone
   *  answers is up to each call's own line (`AgentLine.force_auto_answer`). */
  force_auto_answer: boolean;
  /** `null` until the ACD has a record of them. */
  state: AgentStateRecord | null;
}

export type AgentAction = "login" | "logout" | "pause" | "unpause";

/** `POST /acd/calls/{call_id}/hold`: the hold is on its way to the ACD, not done. */
export interface CallHoldResponse {
  call_id: string;
  held: boolean;
}

/** A supervisor's leg into a call (ADR-029). `monitor_id` is that leg's channel uuid, which is
 *  also the call id the supervisor's phone rings with. */
export interface MonitorResponse {
  monitor_id: string;
  call_id: string | null;
  mode: string | null;
}

/** What `POST /acd/agent/{action}` answers: the command went out, not that it was applied. */
export interface AgentActionResponse {
  command: string;
  user: string;
  domain: string;
  queue: string | null;
}

/**
 * A setter's request to send their caller to a closer, and how far it has got (ADR-016). Mirrors
 * `HandoffResponse`. The note is the setter's words for the closer and never leaves Postgres.
 */
export interface Handoff {
  id: string;
  /** The caller's channel. */
  call_id: string;
  campaign_id: string | null;
  lead_id: string | null;
  /** The setter who sent it, by extension and realm. */
  from_user: string;
  from_domain: string;
  /** The line the caller waits on. */
  queue_name: string;
  required_skills: SkillRequirement[];
  target_user: string | null;
  target_domain: string | null;
  note: string | null;
  /** `requested`, `waiting`, `offered`, `consulting`, `connected`, `cancelled`, `abandoned` or
   *  `failed`. `consulting` and `cancelled` belong to a warm handoff (ADR-027). */
  status: string;
  to_user: string | null;
  to_domain: string | null;
  reason: string | null;
  requested_at: string;
  waiting_at: string | null;
  connected_at: string | null;
  ended_at: string | null;
  expired_at: string | null;
  wait_seconds: number | null;
}

/** One rep on the line a handoff of the rep's call would wait in (ADR-031). */
export interface HandoffCloser {
  /** What `POST /handoffs` names them by. */
  sip_endpoint_id: number;
  user: string;
  domain: string;
  display_name: string | null;
  /** slug → level, what the ACD matches against. */
  skills: Record<string, number>;
  /** The ACD's state of them on that line — the states of `AgentStateRecord` — or `signed_out`
   *  for a rep the line has no record of. */
  state: string;
  on_line: boolean;
  /** Keeps to My calls only on that line (ADR-037): reached by name, never as "any closer". */
  own_calls_only?: boolean;
}

export interface HandoffSkill {
  slug: string;
  name: string;
  category: string | null;
}

/** `GET /handoffs/closers`: who the caller can be sent to, and the skills to narrow them by. */
export interface HandoffClosers {
  queue_id: number;
  queue_name: string;
  campaign_id: string | null;
  /** The tenant's rep skills other than `closer`, which every handoff asks for. */
  skills: HandoffSkill[];
  closers: HandoffCloser[];
}

/** One caller's time on a line, as the event-tracker wrote it. Mirrors `QueueSessionResponse`
 *  (the fields the console reads); a rep reads only their own. */
export interface QueueSession {
  session_id: string;
  /** The caller's channel: what `POST /acd/calls/{id}/disposition` names. */
  channel_uuid: string;
  queue_name: string;
  campaign_id: string | null;
  lead_id: string | null;
  handoff_id: string | null;
  direction: string | null;
  kind: string | null;
  caller_number: string | null;
  caller_name: string | null;
  /** The rep whose own number the caller dialed (`user@domain`, ADR-037). */
  meant_for?: string | null;
  joined_at: string;
  exited_at: string | null;
  outcome: string | null;
  agent_user: string | null;
  agent_domain: string | null;
  bridge_started_at: string | null;
  bridge_ended_at: string | null;
  talk_seconds: number | null;
  disposition: string | null;
  disposition_at: string | null;
}

/** What `POST /acd/calls/{id}/disposition` answers: the outcome went out, as a 202. */
export interface CallDispositionResponse {
  call_id: string;
  session_id: string | null;
  disposition: string;
  lead_id: string | null;
  /** Whether the contract's `disposition.set` went out too; only a call with a lead has one. */
  contract_event: boolean;
  /** A compliance outcome marked the lead do-not-call. */
  dnc?: boolean;
  /** When the lead is called back, for a callback outcome on a call with a lead (ADR-080). */
  callback_at?: string | null;
}

// --- the dialer on the rep's phone ---------------------------------------------------------------
//
// Mirrors `DialerOfferResponse`, `DialerCallResponse`, `OfferedLead`, `DialerActionResponse` and
// `VoicemailMessageResponse` in core-api's `schemas.py` (ADR-019).

/** A lead as the dialer offers it: what a rep reads before choosing Dial or Skip. */
export interface OfferedLead {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  state: string | null;
  language: string | null;
  timezone: string | null;
  /** The list's other columns, as imported. */
  attributes: Record<string, unknown> | null;
}

/** The lead the dialer offers this rep's phone in preview. */
export interface DialerOffer {
  /** What Dial and Skip name. */
  campaign_lead_id: number;
  campaign_id: string;
  campaign_name: string | null;
  offered_at: string | null;
  /** When the lead goes back to the pool unless the rep has dialed it. */
  expires_at: string | null;
  attempts: number;
  /** The slug of the outcome the lead's last call ended with. */
  last_disposition: string | null;
  lead: OfferedLead;
}

/**
 * `GET /dialer/current`: the lead the dialer is calling for this rep's phone, from the dial until
 * they are brought to the rep. Power mode offers nothing first, so this is where the phone learns
 * who it is calling. `attempts` and `last_disposition` are the list row's, as an offer's are: the
 * calls before this one.
 */
export interface DialerCall {
  call_id: string;
  stage: "dialing" | "ringing" | "answered";
  dial_mode: string;
  campaign_lead_id: number | null;
  campaign_id: string | null;
  campaign_name: string | null;
  attempts: number;
  last_disposition: string | null;
  started_at: string;
  lead: OfferedLead;
}

/** How far a call the rep placed by hand through their campaign has got (ADR-047). */
export type ManualCallStage = "requested" | "dialing" | "ringing" | "answered" | "connected" | "ended";

/** `POST /dialer/calls` and `GET /dialer/calls/{id}`: a number the rep dialed by hand, placed the
 *  way the dialer places a preview dial, followed by the id core-api minted for it. */
export interface ManualCall {
  call_id: string;
  stage: ManualCallStage;
  phone: string;
  campaign_id: string;
  campaign_name: string | null;
  lead: OfferedLead;
  /** The number the call presents, once the dialer has decided it. */
  caller_id: string | null;
  /** `call.ended`'s outcome and the switch's hangup cause, once the call is over — or, for a call
   *  that never left, the dialer's own reason (`DNC`, `NO_TRUNK`, `NO_CALLER_ID`). */
  outcome: string | null;
  hangup_cause: string | null;
  started_at: string | null;
  answered_at: string | null;
  bridged_at: string | null;
  ended_at: string | null;
}

// --- a lead's card on the rep's phone --------------------------------------------------------------
//
// Mirrors `LeadCardResponse`, `LeadCardCampaign` and `LeadCardCall` in core-api's `schemas.py`
// (ADR-024).

/** The lead on one campaign's list. */
export interface LeadCardCampaign {
  campaign_id: string;
  campaign_name: string | null;
  state: string;
  attempts: number;
  /** How many calls the lead may get in all: the campaign's cadence's total attempts (ADR-067). */
  max_attempts?: number | null;
  /** The slug of the outcome the lead's last call on this list ended with. */
  last_disposition: string | null;
  last_attempt_at: string | null;
  next_attempt_at: string | null;
  /** The lead's hold, a trigger's (an appointment booked) or a hold disposition's: not dialed before it. */
  hold_until: string | null;
  hold_reason?: string | null;
  /** When the row leaves the list: the list horizon, or an exit (ADR-046). */
  retire_at?: string | null;
  list_id?: number | null;
  list_name?: string | null;
  /** The Dial column's word: the dialer's state, or `held` | `retired` | `on_call` | `do_not_call`
   *  when core-api's columns or the lead say more (`contacts.dial_label` in core-api). */
  dial?: string | null;
  /** The earliest the row is dialable again; null when nothing keeps it. */
  next_at?: string | null;
}

/** A firm as a lead names one (ADR-045). */
export interface FirmRef {
  id: number;
  code: string;
  name: string;
  /** "[CA] Larry H. Parker" */
  label: string;
}

/** Another record with the same phone in the tenant: a duplicate, or the same person for another firm. */
export interface LeadSibling {
  id: string;
  stage: string | null;
  dnc: boolean;
  firm: FirmRef | null;
  created_at: string;
}

/** One of the lead's calls: a stay in a line, or a dial that never reached one. */
export interface LeadCardCall {
  at: string;
  via: "queue" | "dialer";
  campaign_id: string | null;
  campaign_name: string | null;
  direction: string | null;
  /** `inbound`, `outbound` or `handoff` for a queue call; `preview` or `power` for a dial. */
  kind: string | null;
  queue: string | null;
  /** The rep who took it, or was dialed for it (user@domain), and their account's name. */
  rep: string | null;
  rep_name: string | null;
  outcome: string | null;
  disposition: string | null;
  wait_seconds: number | null;
  talk_seconds: number | null;
  /** The lead leg's channel uuid: what `/cdrs?lead_id=`, the recording and the transcript are keyed on. */
  call_id?: string | null;
  has_recording?: boolean;
  has_transcript?: boolean;
}

/** `GET /leads/{id}`: a lead as the person working it sees it, and as the contact record shows it. */
export interface LeadCard {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  email?: string | null;
  state: string | null;
  language: string | null;
  /** IANA: the lead's own clock. */
  timezone: string | null;
  /** The clock the dialer calls them on: their own zone, else their state's, else the calling
   *  campaign's. A callback's time is picked on it (ADR-080). */
  clock_timezone?: string | null;
  source: string;
  source_ref?: string | null;
  /** Resolved from the firm tag the lead carries (ADR-045); null is "Needs firm". */
  firm?: FirmRef | null;
  stage: string | null;
  /** When the lead entered its stage; null when no history row says. */
  stage_since?: string | null;
  /** For a lead in an exit stage an admin may reopen (ADR-076): the stage a reopen restores unless
   *  the admin picks another. Null when there is nothing to reopen, or the lead is do-not-call. */
  reopen_stage?: string | null;
  /** The voicemail message this lead should hear, chosen server-side from their stage and
   *  language (ADR-061). The rep's phone leads with it; they may still pick another. */
  suggested_voicemail?: string | null;
  /** The lead's last disposition, on any campaign. */
  disposition: string | null;
  dnc: boolean;
  consent_wording?: string | null;
  consent_captured_at: string | null;
  attributes: Record<string, unknown> | null;
  created_at: string;
  updated_at?: string | null;
  /** Where the lead is worked: the firm's dedicated campaign, else the tenant's calling one. */
  calling_campaign?: { id: string; name: string; slug: string } | null;
  campaigns: LeadCardCampaign[];
  siblings?: LeadSibling[];
  /** Newest first, at most ten. */
  calls: LeadCardCall[];
  /** The callback promised and not yet followed by an outcome, upcoming or due (ADR-080). */
  callback?: LeadCallback | null;
}

// --- contacts (ADR-044) ----------------------------------------------------------------------------
//
// Mirrors `ContactRow`, `ContactSummary`, `LeadNoteResponse` and `LeadTimelineEntry` in core-api's
// `schemas.py`.

/** One line of `/contacts`: the person's stage beside one campaign's list row, never merged. */
export interface ContactRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  email: string | null;
  firm: FirmRef | null;
  state: string | null;
  language: string | null;
  timezone: string | null;
  stage: string | null;
  stage_since: string | null;
  /** The last outcome, on any campaign, and the dialer's time of the last attempt. */
  disposition: string | null;
  last_outcome_at: string | null;
  dnc: boolean;
  consent_captured_at: string | null;
  source: string;
  created_at: string;
  /** The calling campaign's list row, else the newest. */
  membership: LeadCardCampaign | null;
  dial: string | null;
  next_at: string | null;
  /** The newest of: a dial attempt, a note, a stage move. */
  last_activity_at: string | null;
}

export interface ContactStageCount {
  stage: string;
  count: number;
  /** Leads of the stage with a list row the dialer would take now, but for the clock. */
  due_now: number;
}

/** `GET /leads/summary`: the tiles over the list, over the same filter minus the stage. */
export interface ContactSummary {
  total: number;
  by_stage: ContactStageCount[];
  no_stage: number;
  needs_firm: number;
  dnc: number;
  unassigned: number;
  exhausted: number;
  new_today: number;
  signed_this_month: number;
  due_now: number;
}

export type NoteKind = "note" | "disposition" | "handoff" | "system";

/** A note on a lead (`lead_notes`). */
export interface LeadNote {
  id: number;
  lead_id: string;
  kind: NoteKind;
  /** rep (a signed-in person, any role) | system | ghl */
  author: string;
  by_user_id: number | null;
  by: string | null;
  body: string;
  call_id: string | null;
  campaign_id: string | null;
  handoff_id: string | null;
  /** The outcome a `disposition` note came with. */
  disposition: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeadStageChange {
  from_stage: string | null;
  to_stage: string;
  /** `disposition:<slug>` | `trigger:<id>` | `sibling:<lead id>` | `created` | `import:<id>` |
   *  `reopen: <reason>` | `stage_removed:<slug>` (ADR-077) | … */
  reason: string;
}

export interface LeadTimelineEvent {
  type: string;
  producer: string;
  payload: Record<string, unknown>;
}

export type TimelineKind = "call" | "note" | "stage" | "system" | "callback";

/**
 * A callback promised to the lead (ADR-080), as the record shows it: `upcoming` still ahead, `due`
 * past its time with no outcome since, `done` followed by an outcome (`closed_by`), `rescheduled`
 * replaced by a newer callback, `cancelled` moot since the lead left for an exit.
 */
export interface LeadCallback {
  id: number;
  /** The outcome that promised it. */
  disposition: string;
  due_at: string;
  /** The rep picked the time; false: the outcome's own wait from when it was set. */
  picked: boolean;
  status: "upcoming" | "due" | "done" | "rescheduled" | "cancelled";
  /** The rep it is kept for; null when nobody had the lead. */
  rep: string | null;
  /** Who set it. */
  by: string | null;
  by_user_id: number | null;
  call_id: string | null;
  campaign_id: string | null;
  created_at: string;
  closed_at: string | null;
  closed_by: string | null;
}

/** One thing that happened to the lead; `kind` says which body is set. */
export interface LeadTimelineEntry {
  key: string;
  kind: TimelineKind;
  at: string;
  call_id: string | null;
  by: string | null;
  by_user_id: number | null;
  call: LeadCardCall | null;
  note: LeadNote | null;
  stage: LeadStageChange | null;
  event: LeadTimelineEvent | null;
  /** A callback, at the moment it was promised (ADR-080). */
  callback?: LeadCallback | null;
}

/** `GET /leads/{id}/timeline`: newest first; `next_before` pages further back. */
export interface LeadTimeline {
  entries: LeadTimelineEntry[];
  next_before: string | null;
}

/** `POST /leads/{id}/reopen` (ADR-076): an admin took the lead out of its exit stage. */
export interface LeadReopen {
  lead_id: string;
  stage: string;
  /** The lead and the contacts with its phone that left with it. */
  reopened: string[];
  /** Dial-list rows put back on their lists. */
  lists_restored: number;
}

/** `POST /leads/{id}/disposition`: an outcome recorded off a call. */
export interface ContactOutcome {
  lead_id: string;
  disposition: string;
  stage: string | null;
  stage_moved: boolean;
  dnc: boolean;
  held: number;
  held_until: string | null;
  contract_event: boolean;
  /** When the lead is called back, for a callback outcome (ADR-080). */
  callback_at?: string | null;
  /** The campaign the outcome handed the lead to, when it names one and the handover was made. */
  handed_to_campaign_id?: string | null;
  handed_to_campaign?: string | null;
}

export interface ContactBulkResult {
  action: string;
  applied: number;
  unchanged: number;
  skipped: string[];
}

/** A firm (ADR-045), as `GET /firms` lists them. */
export interface Firm {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  label: string;
  states: string[];
  languages: string[];
  status: string;
  pause_reason: string | null;
  settings: Record<string, unknown>;
  tags: { id: number; tag: string }[];
  contacts: number;
  /** Its book by stage group, the rows its pause holds, the signatures this month. */
  counts: {
    contacts: number;
    working: number;
    scheduled: number;
    exit: number;
    held: number;
    signed_this_month: number;
  };
  dedicated_campaign_ids: string[];
  created_at: string;
  updated_at: string;
}

// --- the live floor --------------------------------------------------------------------------------
//
// Mirrors `FloorResponse`, `FloorLine`, `AcdStatsResponse`, `LineStats` and `AgentStats` in core-api's
// `schemas.py`; a floor's reps and callers are the event-tracker's records (`WaitingRecord` in
// `services/shared/state/queues.py`), passed through as they are — hence camelCase.

/** A rep as the floor shows them: their live record, and the name on their account. */
export type FloorRep = AgentStateRecord & {
  displayName: string | null;
  /** The name of the contact on the call on hand (`GET /acd/floor` reads it off the lead); null
   *  for a contact with no name. The lead of a dial the dialer is still placing counts. */
  contactName?: string | null;
};

/** A caller waiting in a line now. */
export interface WaitingCaller {
  channelId: string;
  queue: string;
  direction: string;
  sessionId?: string | null;
  /** `inbound`, `outbound` (a lead the dialer reached) or `handoff`. */
  kind?: string | null;
  joinedAt?: string | null;
  callerNumber?: string | null;
  callerName?: string | null;
  leadId?: string | null;
  /** The contact's own name, where the caller is a contact (`GET /acd/floor`). */
  contactName?: string | null;
  campaignId?: string | null;
  handoffId?: string | null;
  priority?: number | null;
  requiredSkills?: Record<string, number>;
  preferredSkills?: Record<string, number>;
  /** 1 for the next caller the line serves. */
  position?: number | null;
  /** Who the caller is for (`user@domain`): they dialed that rep's own number (ADR-037). */
  meantFor?: string | null;
  /** Nobody but that rep may take them. */
  meantForOnly?: boolean;
  /** Until when the line holds them for that rep; null once anyone may take them. */
  pinnedUntil?: string | null;
}

/** One line on the floor: its callers now, first in first. */
export interface FloorLine {
  id: number | null;
  name: string;
  campaign_id: string | null;
  campaign_name: string | null;
  waiting: number;
  longest_wait_seconds: number | null;
  callers: WaitingCaller[];
}

/** `GET /acd/floor`: every rep and line in the supervisor's reach, live. */
export interface AcdFloor {
  reps: FloorRep[];
  lines: FloorLine[];
  handoffs_pending: number;
}

/** One line's callers over a window, as `src/acd_stats.py` counts them. Rates are 0–1, and null
 *  where there was nothing to divide. */
export interface LineStats {
  queue: string;
  offered: number;
  connected: number;
  dropped: number;
  abandoned: number;
  timed_out: number;
  moved: number;
  removed: number;
  in_progress: number;
  handoffs: number;
  relaxed: number;
  ronas: number;
  answer_rate: number | null;
  abandon_rate: number | null;
  service_level: number | null;
  wait_avg: number | null;
  wait_p50: number | null;
  wait_p90: number | null;
  wait_max: number | null;
  abandon_wait_avg: number | null;
  ring_avg: number | null;
  ring_p90: number | null;
  ring_max: number | null;
  talk_avg: number | null;
  talk_total: number;
  offers_avg: number | null;
}

/** One rep's calls over a window, and their time in each effective state. */
export interface AgentStats {
  user: string;
  domain: string;
  calls: number;
  talk_total: number;
  talk_avg: number | null;
  ring_avg: number | null;
  ronas: number;
  dispositioned: number;
  /** Effective state → seconds, clipped to the window. */
  time_in_state: Record<string, number>;
}

/** `GET /acd/stats`: the callers who joined in the window. */
export interface AcdStats {
  since: string;
  until: string;
  sla_seconds: number;
  totals: LineStats;
  lines: LineStats[];
  agents: AgentStats[];
}

/** What the dialer's actions answer: it was told, as a 202, not that it has done it. */
export interface DialerActionResponse {
  command: string;
  campaign_lead_id: number | null;
  call_id: string | null;
}

/** A message a rep leaves on a lead's voicemail. */
export interface VoicemailMessage {
  id: number;
  tenant_id: number;
  /** What a drop names. Fixed at create: core-api has no way to rename one. */
  slug: string;
  name: string;
  /** `en` or `es`, shown beside the message on a rep's phone; null when it is not marked. */
  language: string | null;
  /** Who it is for: a lead stage (ADR-061), or null for the tenant's general message. */
  stage: string | null;
  /** What the switch plays: a file from the audio store by its URL, or — from before the store — a
   *  sound file on the node or a stream it makes itself. */
  media: string;
  /** False once retired: off reps' phones, and a drop that names it is refused. */
  active: boolean;
  created_at: string;
}

// --- skills (ADR-015) ----------------------------------------------------------------------------
//
// Mirrors `RoutingSkillResponse`, `UserSkill` and `UserSkillsResponse` in core-api's `schemas.py`.

/**
 * A skill in the tenant's catalog: `setter`, `closer`, `spanish`. A line or a number requires or
 * prefers skills, a handoff asks for `closer`, and the ACD rings only a rep who holds each one it
 * requires at the level asked.
 */
export interface RoutingSkill {
  id: number;
  tenant_id: number;
  /** Immutable: lines, numbers, handoffs and reps all name a skill by it. */
  slug: string;
  name: string;
  /** `role`, `language`, or whatever the tenant groups by. */
  category: string | null;
  description: string | null;
  /** Reps can hold it, and a line, a number or a handoff can require it. */
  is_agent_skill: boolean;
  /** It describes a lead: the dialer turns a lead's tag into a preference or a requirement. */
  is_lead_tag: boolean;
  /** False once retired. Reps keep their level for the day it comes back; the ACD ignores it. */
  active: boolean;
  /** Reps holding it, at any level. */
  holders: number;
}

/** One skill a rep holds, at a level from 1 to 5. */
export interface UserSkill {
  slug: string;
  level: number;
}

/** Where an invitation stands (ADR-028 in `platform`): waiting, past its 72 hours, or used. */
export type InvitationStatus = "pending" | "expired" | "accepted";

/** `GET /invitations`: somebody an admin has asked to join, and who they will be once they do. */
export interface Invitation {
  id: number;
  email: string;
  display_name: string | null;
  role: Role;
  tenant_id: number | null;
  tenant_name: string | null;
  domain_id: number | null;
  domain_name: string | null;
  /** Where an admin's or superadmin's phone goes. Null for the roles locked to a domain. */
  endpoint_domain_id: number | null;
  endpoint_domain_name: string | null;
  /** Each is staffed on its primary line at acceptance. `name` is null once it has been deleted. */
  campaigns: { id: string; name: string | null }[];
  skills: UserSkill[];
  status: InvitationStatus;
  expires_at: string;
  /** How many times it has been sent, the first time included. */
  sent_count: number;
  last_sent_at: string;
  /** When the SMTP server took the last send's email. Null with `email_error` null: none was tried. */
  emailed_at: string | null;
  email_error: string | null;
  invited_by_id: number | null;
  invited_by_name: string | null;
  accepted_at: string | null;
  accepted_user_id: number | null;
  created_at: string;
}

/** The answer to sending or sending again: the only time an invitation's link comes back. */
export interface InvitationSent extends Invitation {
  invite_url: string;
  /** Whether this server emails invitations at all. False: the admin sends the link themselves. */
  email_enabled: boolean;
}

/** One person of `POST /invitations/bulk`: their invitation, or why none was made. */
export interface InvitationBulkResult {
  email: string;
  /** The invitation made for this address, with its link. Null when none was. */
  invitation: InvitationSent | null;
  /** Why none was: the address already has an account, or an invitation not yet accepted. */
  error: string | null;
}

/** The answer to inviting several people: a result for each, in the order they were given. */
export interface InvitationBulkSent {
  results: InvitationBulkResult[];
  /** Whether this server emails invitations at all. False: the admin sends the links themselves. */
  email_enabled: boolean;
}

/** `POST /auth/invitation`: what the person is joining, for the page their link opens. */
export interface InvitationPreview {
  email: string;
  display_name: string | null;
  role: Role;
  tenant_name: string | null;
  invited_by_name: string | null;
  /** The campaigns they will staff, by name. */
  campaigns: string[];
  skills: { slug: string; name: string; level: number }[];
  status: InvitationStatus;
  expires_at: string;
}

/** `POST /app-users/{id}/sign-in-link` (ADR-040 in `platform`): the one time a sign-in link is
 *  shown. It signs a browser in as the person, once, until `expires_at`. */
export interface SignInLinkMade {
  url: string;
  expires_at: string;
  user_id: number;
  email: string;
  display_name: string | null;
}

/** `GET /app-users/{id}/skills`: a rep's skills, on the phone the ACD rings. */
export interface UserSkills {
  user_id: number;
  /** The SIP identity the ACD knows the rep by; null for an account with no phone. */
  user: string | null;
  domain: string | null;
  skills: UserSkill[];
}

/** One setting that plays a store file (`AudioUse` in core-api). */
export interface AudioUse {
  /** `line`, `campaign` or `voicemail_message`. */
  kind: string;
  id: string;
  name: string;
  /** `moh`, `entry_announcement`, `outbound_hold_media`, `safe_harbor_media` or `media`. */
  setting: string;
  /** The campaign to open to change it; null for a voicemail message or a standalone line. */
  campaign_id: string | null;
}

/** A file in the tenant's audio store (ADR-020 in platform). */
export interface AudioFile {
  id: number;
  /** What the file's URL names it by. */
  uuid: string;
  tenant_id: number;
  name: string;
  description: string | null;
  tags: string[];
  mime: string;
  size_bytes: number;
  /** Read off the WAV header at upload; null when the header did not say. */
  duration_ms: number | null;
  sample_rate: number | null;
  channels: number | null;
  created_at: string;
  updated_at: string;
  /** What a setting stores and the switch fetches — and what the console plays to preview it. */
  url: string;
  /** Every setting that plays it. A file in use cannot be deleted. */
  used_by: AudioUse[];
}

// --- numbers, routing and campaigns ------------------------------------------------------------
//
// Mirrors `services/core-api/src/schemas.py` (QueueResponse, CampaignResponse,
// PhoneNumberResponse, NumberOrderResponse, DidResponse) and `src/numbers.py` (ROUTE_TYPES).

/** One skill a line asks of a rep, at a minimum level (ADR-015). */
export interface SkillRequirement {
  slug: string;
  min_level: number;
}

/**
 * An ACD line (a `queues` row): where a call waits for a rep, and the settings it waits under.
 * A campaign owns its lines (ADR-014) and creates its primary one with itself; a standalone line
 * is a routing target a number names directly.
 */
export interface Queue {
  id: number;
  /** The `acl_queue` value. Fixed once calls and reps use it, even if the campaign is renamed. */
  name: string;
  description: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  /** `primary`, `closer`, `inbound` or `custom`; null for a standalone line. */
  purpose: string | null;
  enabled: boolean;
  ring_timeout_seconds: number;
  /** one_at_a_time | ring_all — how many reps one offer rings (ADR-049). */
  ring_strategy: string;
  /** A caller who calls back rings the rep who last spoke to them first, when that rep is free
   *  (ADR-072). */
  previous_rep_first: boolean;
  wrap_up_seconds: number;
  disposition_seconds: number;
  /** 0 = a caller waits as long as it takes. */
  max_wait_seconds: number;
  moh: string | null;
  outbound_hold_media: string | null;
  entry_announcement: string | null;
  required_skills: SkillRequirement[];
  preferred_skills: SkillRequirement[];
  skill_strategy: string;
  skill_relax_seconds: number;
  default_priority: number;
  /** Who takes an inbound caller we know, by their contact's stage (ADR-073). */
  stage_rules: StageRule[];
  member_count: number;
}

/** One of the tenant's stages (ADR-077). Mirrors core-api's `StageResponse`. */
export interface StageRow {
  slug: string;
  label: string;
  description: string | null;
  group: "working" | "scheduled" | "exit";
  /** An exit's reach: every record with the phone, or only the same firm's. */
  exit_scope: "all_firms" | "same_firm" | null;
  /** The dialer ranks a lead in it near the top, as About to sign always was. */
  dial_boost: boolean;
  sort_order: number;
  /** One of the platform's four: renamed and reordered, never deleted. */
  system: boolean;
  /** The tenant's leads in it now. */
  leads: number;
}

/** What deleting a stage moved to its replacement (`DELETE /stages/{slug}`). */
export interface StageRemoved {
  slug: string;
  replaced_by: string | null;
  leads: number;
  dispositions: number;
  line_rules: number;
  voicemail_messages: number;
}

/** A line's rule for callers in one stage (ADR-073). Mirrors core-api's `StageRule`: `only` reps
 *  with `skills` may take the caller, or those reps ring `first` and then anyone. For that caller it
 *  replaces the line's skills and the number's; their language still counts. */
export interface StageRule {
  stage: string;
  mode: "only" | "first";
  skills: SkillRequirement[];
  /** `only`: after this many seconds anyone on the line may take the caller too. Null: they wait
   *  for the rule's reps, up to the line's own wait limit. */
  fallback_seconds: number | null;
}

/**
 * A law firm's intake programme: firm + state + language, the lines its calls are served from,
 * and how the dialer works its lists (ADR-019). Mirrors `CampaignResponse`.
 */
export type RepNumberFallback = "line" | "rep_only";

export interface Campaign {
  id: string;
  tenant_id: number;
  tenant_name: string | null;
  name: string;
  /** The CRM's firm tag (`lhpark_ca`) — what intake resolves to this row. */
  slug: string;
  state: string | null;
  language: string;
  /** The primary line. core-api sets it when it creates the campaign; no client moves it. */
  queue_id: number | null;
  queue_name: string | null;
  /** Inbound routing: a disabled campaign's numbers announce and hang up. */
  enabled: boolean;
  /** `draft`, `active`, `paused` or `done`. Only an active campaign is dialed. */
  status: string;
  /** inbound | outbound | both (ADR-060). */
  purpose: string;
  dial_mode: string;
  language_routing: string;
  force_auto_answer: boolean;
  /** Which of a rep's dialing campaigns gives them their next lead; higher first (ADR-054). */
  dial_priority: number;
  dial_timeout_seconds: number;
  preview_timeout_seconds: number;
  /** HH:MM on the lead's own clock. */
  calling_window_start: string;
  calling_window_end: string;
  respect_lead_timezone: boolean;
  default_timezone: string | null;
  abandon_after_seconds: number;
  safe_harbor_action: string;
  safe_harbor_media: string | null;
  /** In order; empty = each rep's domain routing. */
  outbound_gateway_ids: number[];
  /** What every dialer call presents; null = the rep's own number, then the domain's default. */
  caller_id_number: string | null;
  /** 0 = a lead never ages off a list. */
  list_horizon_days: number;
  /** What happens when a caller dials a rep's own number here and that rep cannot take the call
   *  (ADR-037): `line`, anyone on the line after the wait; `rep_only`, nobody else, and the
   *  closing message ends the call. */
  rep_number_fallback: RepNumberFallback;
  rep_number_wait_seconds: number;
  rep_number_message: string | null;
  /** A rep with their own number here presents it on their dialer calls for the campaign. */
  rep_number_caller_id: boolean;
  /** Its reps may choose My calls only. */
  own_calls_only_allowed: boolean;
  /** The cadence the campaign runs (ADR-066, ADR-067): its only dialing policy, so the attempts a
   *  lead gets, the waits between them and the daily cap on a number are its. Null only while the
   *  dialer is off; a dial mode with no cadence is refused. */
  cadence_id: number | null;
  cadence: CadenceRef | null;
  /** What an admin should know about the cadence as it stands; said, not refused. */
  cadence_warnings: string[];
  /** The primary line, with its settings. */
  routing: Queue | null;
  /** Every line the campaign owns, the primary among them. */
  lines: Queue[];
  did_count: number;
  created_at: string;
  updated_at: string;
}

/** Who staffs a line: the phone the ACD rings and the person holding it. Mirrors
 *  `QueueMemberResponse`. */
export interface QueueMember {
  queue_id: number;
  queue_name: string;
  user: string;
  domain: string;
  enabled: boolean;
  sip_endpoint_id: number | null;
  app_user_id: number | null;
  display_name: string | null;
  /** The skills the ACD sees on this rep: slug → level. */
  skills: Record<string, number>;
}

/** A call outcome in the tenant's catalog (ADR-017). Mirrors `DispositionResponse`. */
export interface Disposition {
  id: number;
  tenant_id: number;
  /** Immutable: the contract's `disposition.set`, the CRM's tags and the reports carry it. */
  slug: string;
  label: string;
  /** What choosing it does to the lead: `terminal`, `callback`, `retry` or `hold`. */
  kind: string;
  category: string | null;
  backoff_seconds: number | null;
  hold_seconds: number | null;
  /** The campaign this outcome hands the lead to; null hands them nowhere (ADR-056). */
  enlist_campaign_id: string | null;
  requires_note: boolean;
  sort_order: number;
  active: boolean;
  /** The stage this outcome puts the lead in (ADR-046); null keeps it. */
  stage?: string | null;
}

/** An uploaded dial list, and what its import made of the file. Mirrors `LeadListResponse`. */
export interface LeadList {
  id: number;
  campaign_id: string;
  name: string;
  source: string;
  /** `importing` until the background import writes its counts, then `ready` or `failed`. */
  status: string;
  total_rows: number;
  imported: number;
  duplicates: number;
  skipped: number;
  /** Why rows were skipped: reason → rows. */
  skip_reasons: Record<string, number>;
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

// --- webhooks (ADR-022) ----------------------------------------------------------------------------
// Mirrors `WebhookEndpointResponse`, `WebhookSubscriptionResponse`, `WebhookDeliveryResponse` and
// `WebhookEventTypeResponse` in core-api's `schemas.py`.

/** A URL of the tenant's that the platform sends events to. Its signing secret is never read back. */
export interface WebhookEndpoint {
  id: number;
  tenant_id: number;
  name: string;
  kind?: "http" | "slack";
  slack_connection_id?: number | null;
  slack_channel_id?: string | null;
  slack_channel_name?: string | null;
  url: string | null;
  /** POST | PUT | PATCH */
  method: string;
  /** Sent with every delivery, values included: a receiver's own API key, say. */
  headers: Record<string, string>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/** The answer to a create: the one time the secret is shown. */
export interface WebhookEndpointCreated extends WebhookEndpoint {
  secret: string;
}

/** One event type an endpoint hears, from every campaign or from one. */
export interface WebhookSubscription {
  id: number;
  endpoint_id: number;
  tenant_id: number;
  event_type: string;
  /** `tenant`: every campaign's events. `campaign`: only `source_id`'s. */
  source_type: string;
  source_id: string | null;
  /** {dotted path into the event: the value it must hold}, every one of them. */
  conditions: Record<string, string | number | boolean | null>;
  /** `event`: the event as JSON. `template`: `template` rendered over it. */
  post_in?: "thread" | "channel" | null;
  body_mode: string;
  template: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/** One event on its way to one subscription's endpoint. A page of the log carries no events. */
export interface WebhookDelivery {
  id: number;
  subscription_id: number;
  endpoint_id: number;
  tenant_id: number;
  event_id: string;
  slack_message_url?: string | null;
  slack_ts?: string | null;
  slack_thread_ts?: string | null;
  slack_channel_id?: string | null;
  event_type: string;
  /** pending | delivered | failed | dead */
  status: string;
  attempts: number;
  next_attempt_at: string | null;
  last_attempt_at: string | null;
  last_status: number | null;
  last_error: string | null;
  replay_requested_at: string | null;
  /** A sample an admin sent to try the endpoint, not an event that happened. */
  test: boolean;
  created_at: string;
  delivered_at: string | null;
}

/** One delivery read on its own: its event as it was queued, until the retention period empties it. */
export interface WebhookDeliveryDetail extends WebhookDelivery {
  payload: Record<string, unknown> | null;
}

/** An event type a subscription can name, with the contract's own example of it. */
export interface WebhookEventType {
  builtin_message?: boolean;
  type: string;
  sample: Record<string, unknown> | null;
}

// --- triggers (ADR-021) ----------------------------------------------------------------------------
// Mirrors `TriggerResponse`, `TriggerCreated`, `TriggerSecretResponse`, `TriggerInboxRowResponse` and
// `TriggerInboxDetailResponse` in core-api's `schemas.py`.

/** A URL another system calls when something happens there, and what the platform does about it. */
export interface Trigger {
  id: number;
  tenant_id: number;
  name: string;
  description: string | null;
  /** capture | lead.hold | lead.release | lead.delist | lead.dnc | lead.enlist */
  action: string;
  /** Where a sender posts, after the API's own address: `/hooks/t/{key}`. */
  path: string;
  secret_kind: string;
  /** The header the secret travels in. The secret itself is never read back. */
  secret_header: string;
  /** A dotted path into a request per field, and `idempotency` and `attributes`. */
  mapping: Record<string, unknown>;
  options: Record<string, unknown>;
  campaign_id: string | null;
  lead_source: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  last_received_at: string | null;
}

/** The answer to a create: the one time the secret is shown. */
export interface TriggerCreated extends Trigger {
  secret: string;
}

/** The answer to a rotation. The old secret no longer works. */
export interface TriggerSecret {
  secret: string;
  secret_header: string;
}

/** One request a trigger received. A page of the inbox carries no request body. */
export interface TriggerInboxRow {
  id: number;
  trigger_id: number;
  idempotency_key: string;
  received_at: string;
  /** received | applied | rejected | failed */
  status: string;
  attempts: number;
  /** What the action did, or why it did nothing. */
  result: Record<string, unknown> | null;
  error: string | null;
  applied_at: string | null;
}

/** One request read on its own: its body as it arrived, until the retention period empties it. */
export interface TriggerInboxDetail extends TriggerInboxRow {
  raw: Record<string, unknown> | null;
}

/** A lead on a campaign, and where it stands with the dialer. Mirrors `CampaignLeadResponse`. */
export interface CampaignLead {
  id: number;
  lead_id: string;
  list_id: number | null;
  state: string;
  priority: number;
  attempts: number;
  next_attempt_at: string | null;
  last_attempt_at: string | null;
  last_disposition: string | null;
  last_outcome: string | null;
  offered_to: string | null;
  assigned_rep: string | null;
  hold_until: string | null;
  block_reason: string | null;
  retire_at: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  created_at: string;
  /** The row's place on the campaign's cadence (ADR-066), and the words for why. */
  priority_reason?: string | null;
  next_reason?: string | null;
  cadence_cycle?: number;
  cadence_step?: number;
  technical_retries?: number;
  step_not_after?: string | null;
}

// --- cadences (ADR-066) -----------------------------------------------------------------------------
//
// Mirrors the cadence schemas in core-api's `schemas.py` and the body `services/shared/cadence_body.py`
// validates in `platform`.

export interface CadenceCycle {
  attempts: number;
  between_attempts_seconds?: number[];
  after_cycle_seconds: number;
  initial_delay_seconds?: number;
  did_strategy?: string[];
  voicemail_after_attempt?: number | null;
}

export interface CadencePhase {
  name: string;
  ends_days_after_enrollment?: number | null;
  priority_band: number;
  age_decay_per_day?: number;
  priority_floor?: number | null;
  not_after_seconds?: number | null;
  cycle: CadenceCycle;
}

/** The body an admin saves as a version. Loosely typed: the server validates it. */
export interface CadenceBody {
  clock?: string;
  phases: CadencePhase[];
  opening?: Record<string, unknown>;
  limits?: {
    total_attempts?: number;
    expire_days_after_enrollment?: number | null;
    /** Calls to one number in a day, from any campaign in any mode, a hand dial too; null is no
     *  cap (ADR-067, the meaning ADR-064 gave the campaign's own cap). */
    daily_calls_per_number?: number | null;
  };
  technical?: {
    retry_seconds?: number;
    max_retries?: number;
    technical_causes?: string[];
    invalid_number_causes?: string[];
    invalid_stops_after?: number;
  };
  on_outcome?: {
    not_worked_seconds?: number;
    abandoned_counts?: boolean;
    manual_dial_counts?: boolean;
    inbound_counts?: boolean;
    callback_default_seconds?: number;
    [key: string]: unknown;
  };
  priority?: {
    callback?: number;
    about_to_sign?: number;
    mid_cycle?: number;
    reply_received?: number;
    initial_fresh?: number;
  };
}

export interface CadenceVersion {
  id: number;
  version_n: number;
  body: CadenceBody;
  unsupported_in_v1: string[];
  note: string | null;
  created_by: number | null;
  created_at: string;
}

export interface Cadence {
  id: number;
  tenant_id: number;
  slug: string;
  name: string;
  active: boolean;
  current_version: CadenceVersion | null;
  versions: number;
  campaigns: number;
  created_at: string;
  updated_at: string;
}

/** The cadence a campaign runs, as the campaign shows it. */
export interface CadenceRef {
  id: number;
  slug: string;
  name: string;
  version_n: number | null;
  unsupported_in_v1: string[];
  problem: string | null;
}

export interface CadenceCampaignRef {
  id: string;
  name: string;
  slug: string;
}

export interface LeadCadenceDecision {
  id: number;
  at: string;
  kind: string;
  trigger_key: string;
  from_state: string | null;
  to_state: string | null;
  cycle: number | null;
  step: number | null;
  next_attempt_at: string | null;
  step_not_after: string | null;
  priority: number | null;
  reason: string | null;
  /** Always false since ADR-067; a decision written while a cadence ran in shadow keeps its flag. */
  shadow: boolean;
}

export interface LeadCadenceRequest {
  id: number;
  kind: string;
  until: string | null;
  reason: string | null;
  requested_by: number | null;
  requested_at: string;
  applied_at: string | null;
  applied_result: string | null;
}

/** The lead's row on one campaign, read as the dialer reads it. */
export interface LeadCadenceMembership {
  campaign_lead_id: number;
  campaign_id: string;
  campaign_name: string | null;
  /** `slug@version`, or null on a campaign without a cadence. */
  cadence: string | null;
  state: string;
  dial: string | null;
  next_at: string | null;
  next_attempt_at: string | null;
  next_reason: string | null;
  step_not_after: string | null;
  priority: number;
  priority_reason: string | null;
  cycle: number;
  step: number;
  attempts: number;
  technical_retries: number;
  held_by: string | null;
  phase: string | null;
  expires_at: string | null;
  assigned_rep: string | null;
  last_transition_at: string | null;
  pending_requests: LeadCadenceRequest[];
  decisions: LeadCadenceDecision[];
}

export interface LeadCadence {
  lead_id: string;
  memberships: LeadCadenceMembership[];
}

export interface CadenceActionResult {
  action: string;
  affected: number;
  requests: LeadCadenceRequest[];
}

export interface DialProblem {
  rep: string;
  reason: string;
  first_seen: string;
  last_seen: string;
  count: number;
}

export interface CadenceWorkRow {
  campaign_lead_id: number;
  lead_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  state: string;
  dial: string | null;
  next_attempt_at: string | null;
  next_reason: string | null;
  step_not_after: string | null;
  priority: number;
  priority_reason: string | null;
  cycle: number;
  step: number;
  attempts: number;
  technical_retries: number;
  hold_until: string | null;
  hold_reason: string | null;
  assigned_rep: string | null;
}

export interface CadenceWork {
  campaign_id: string;
  cadence: string | null;
  problem: string | null;
  buckets: { bucket: string; count: number }[];
  bucket: string | null;
  rows: CadenceWorkRow[];
  dial_problems: DialProblem[];
}

export interface CadenceMigrateRow {
  campaign_lead_id: number;
  lead_id: string;
  state: string;
  attempts: number;
  cycle: number;
  step: number;
  phase: string | null;
  priority: number;
  priority_reason: string | null;
  on_cadence: boolean;
}

export interface CadenceMigrate {
  campaign_id: string;
  cadence: string | null;
  total: number;
  rows: CadenceMigrateRow[];
}

export const NUMBER_STATUSES = ["available", "pending_approval", "assigned"] as const;
export type NumberStatus = (typeof NUMBER_STATUSES)[number];

export const NUMBER_STATUS_LABELS: Record<NumberStatus, string> = {
  available: "Available",
  pending_approval: "Requested",
  assigned: "Assigned",
};

/** A number in the platform's pool — the asset, and who holds it. */
export interface PhoneNumber {
  id: number;
  number: string;
  country: string;
  region: string | null;
  locality: string | null;
  area_code: string | null;
  number_type: string;
  capabilities: Record<string, boolean>;
  provider: string;
  notes: string | null;
  status: NumberStatus;
  tenant_id: number | null;
  tenant_name: string | null;
  enabled: boolean;
  /** The routing row, once assigned. */
  did_id: number | null;
  /** The request awaiting a decision, while there is one. */
  pending_order_id: number | null;
  created_at: string;
  updated_at: string;
}

export const ORDER_STATUSES = ["pending_approval", "approved", "rejected", "expired", "cancelled"] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending_approval: "Awaiting approval",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
  cancelled: "Cancelled",
};

/** A tenant's request for one number, kept after it is decided. */
export interface NumberOrder {
  id: number;
  phone_number_id: number;
  number: string;
  tenant_id: number;
  tenant_name: string | null;
  requested_by_user_id: number | null;
  requested_by_email: string | null;
  status: OrderStatus;
  note: string | null;
  requested_at: string;
  expires_at: string | null;
  decided_at: string | null;
  decided_by_user_id: number | null;
  decided_by_email: string | null;
  reject_reason: string | null;
  did_id: number | null;
}

export const ROUTE_TYPES = ["campaign", "queue", "extension", "room", "hangup"] as const;
export type RouteType = (typeof ROUTE_TYPES)[number];

/** The routes that name one rep. They differ in where the caller ends up. */
export const ENDPOINT_ROUTES: readonly RouteType[] = ["extension", "room"];

export const ROUTE_LABELS: Record<RouteType, string> = {
  campaign: "Campaign",
  queue: "Queue",
  extension: "Rep's phone",
  room: "Rep's room",
  hangup: "Hang up",
};

export const ROUTE_DESCRIPTIONS: Record<RouteType, string> = {
  campaign: "Park the caller for the campaign's inbound queue, tagged with the campaign.",
  queue: "Park the caller for a named ACD queue.",
  extension: "Ring one rep's phone. The call fails if they are not signed in.",
  room: "Ring the rep's phone. They answer into a room of the call's own, where they can hold the caller, bring colleagues in and hand the call over.",
  hangup: "Play an announcement, if any, then hang up.",
};


/** A number a tenant holds, and what a call to it does. Mirrors `DidResponse`. */
export interface Did {
  id: number;
  number: string;
  description: string | null;
  /** `null` = not routed yet: an approved number nobody has configured. */
  action_type: RouteType | null;
  /** The queue name for `queue`; the sound file for `hangup`. */
  action_target: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  campaign_slug: string | null;
  campaign_queue_name: string | null;
  sip_endpoint_id: number | null;
  sip_endpoint_extension: string | null;
  sip_endpoint_domain_name: string | null;
  sip_endpoint_user_name: string | null;
  /** A campaign route that names a rep (ADR-037): whether that rep still staffs the campaign.
   *  False: the number rings the campaign's line like its own. Null: no rep named. */
  rep_on_campaign?: boolean | null;
  /**
   * Skills every call to this number needs, on top of the line's own (ADR-015): a Spanish line is
   * a number that needs `spanish`. Only `campaign` and `queue` routes reach the ACD, so the server
   * keeps tags on those two and clears them on the rest.
   */
  routing_tags: SkillRequirement[];
  tenant_id: number | null;
  tenant_name: string | null;
  phone_number_id: number | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * One line saying where a call to this number goes. The table and the route dialog both show
 * it, and the server's shape (`action_type` + three targets) is not something to read at a
 * glance: "Rep · Ana (1001@reps.example)" is.
 */
export function routeSummary(did: Pick<
  Did,
  | "action_type" | "action_target" | "campaign_name" | "campaign_queue_name"
  | "sip_endpoint_extension" | "sip_endpoint_domain_name" | "sip_endpoint_user_name"
> & { sip_endpoint_id?: number | null }): string {
  switch (did.action_type) {
    case "campaign": {
      const name = did.campaign_name ?? "unknown campaign";
      if (!did.campaign_queue_name) return `${name} (no inbound queue — calls will hang up)`;
      // A number that names one of the campaign's reps rings them first (ADR-037).
      if (did.sip_endpoint_id != null) {
        return `${name} → ${did.sip_endpoint_user_name ?? did.sip_endpoint_extension ?? "a rep"}`;
      }
      return `${name} → queue ${did.campaign_queue_name}`;
    }
    case "queue":
      return `Queue ${did.action_target ?? "?"}`;
    // Both routes name the same thing, the rep: the room a `room` call lives in is made for that
    // call and has no name worth printing (ADR-033). The route label says which route it is.
    case "extension":
    case "room": {
      const ext = did.sip_endpoint_extension
        ? `${did.sip_endpoint_extension}@${did.sip_endpoint_domain_name ?? "?"}`
        : "endpoint gone — calls will hang up";
      return did.sip_endpoint_user_name ? `${did.sip_endpoint_user_name} (${ext})` : ext;
    }
    case "hangup":
      return did.action_target ? `Play ${did.action_target}, then hang up` : "Hang up";
    default:
      return "Not routed";
  }
}

/**
 * Whether a route as stored can still take a call — mirrors what the xml gateway checks at
 * call time. The console flags these so an admin finds out before a caller does.
 */
export function routeIsBroken(
  did: Pick<Did, "action_type" | "campaign_queue_name" | "sip_endpoint_id" | "campaign_id">,
): boolean {
  if (did.action_type === "campaign") return !did.campaign_id || !did.campaign_queue_name;
  if (did.action_type && (ENDPOINT_ROUTES as readonly string[]).includes(did.action_type)) {
    return did.sip_endpoint_id === null;
  }
  return false;
}

/**
 * Parse a pasted block of numbers — one per line, or separated by commas — into the strings
 * the server normalises. Blank lines and duplicates are dropped here so the batch the server
 * refuses for a repeat is one the user actually typed twice, not a paste artefact.
 */
export function splitNumbers(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(/[\n,;]+/)) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    // The same approximation of the server's E.164 rule: digits only, and a bare ten-digit US
    // number is the same line as its +1 spelling.
    const digits = trimmed.replace(/\D/g, "");
    const key = digits.length === 10 ? `1${digits}` : digits;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

// --- calls (CDRs) ------------------------------------------------------------------------------
//
// Mirrors `CdrResponse`, `CdrDetailResponse` and `CdrSummaryResponse` in core-api's `schemas.py`.
//
// One row is one **leg**, which is what the switch writes. A conversation between a caller and a
// rep is usually two rows, and they share no id in `cdrs` — so the console counts legs and says
// so, rather than inventing a join key the switch never gave it.

/** What the dialer stamped on the leg. `other` is the fallback for a leg it never touched. */
export const CALL_LEGS = ["lead", "rep", "room", "inbound", "sound_test", "other"] as const;
export type CallLeg = (typeof CALL_LEGS)[number];

export const CALL_LEG_LABELS: Record<CallLeg, string> = {
  lead: "Lead",
  rep: "Rep",
  room: "Room",
  inbound: "Inbound",
  sound_test: "Sound test",
  other: "Unattributed",
};

export const CALL_LEG_DESCRIPTIONS: Record<CallLeg, string> = {
  lead: "The dialled party on an outbound campaign call.",
  rep: "A rep's own leg.",
  room: "A leg inside a rep's conference room.",
  inbound: "A caller arriving on a DID.",
  sound_test: "A rep checking their microphone and speaker from the audio setup dialog.",
  other: "The dialer never stamped this leg — today that is every call a rep places by hand.",
};

/**
 * The reader's own legs that are not calls with anybody, for `exclude_leg` on a personal list:
 * their line into their own conference room, and their sound tests (ADR-062 in `platform`).
 */
export const OWN_NON_CALL_LEGS = "room,sound_test";

/** One finished leg, as the calls table shows it. */
export interface Cdr {
  id: number;
  channel_uuid: string;
  leg: string | null;
  direction: "inbound" | "outbound" | null;

  caller_number: string | null;
  caller_name: string | null;
  callee_number: string | null;
  callee_name: string | null;

  domain: string | null;
  did: string | null;
  campaign_id: string | null;
  /** Resolved server-side. Null for a campaign deleted since the call — the id survives. */
  campaign_name: string | null;
  lead_id: string | null;
  /** The contact this leg was with, resolved server-side from `lead_id`. Neither name column can
   *  stand in for it: the trunk sends no caller-id name, so without this a call from a lead the
   *  firm has held for months reads as a bare number. Null on a leg that names no lead, on one
   *  whose lead has been deleted since, and where the lead has no name. */
  lead_name: string | null;
  /** Which of this leg's two numbers is the contact's, so the name goes against the right one.
   *  Not derivable here: the ACD rings the rep WITH the lead as the caller, so the rep's leg of
   *  an inbound call is an outbound channel carrying the lead in `caller_number`. */
  lead_side: "caller" | "callee" | null;
  /** What the leg was to the ACD: `inbound` (a caller on a line), `outbound` (a lead the dialer
   *  reached, on the rep's leg too), `handoff`; null on a leg that never met the ACD. */
  kind: string | null;
  /** How a dialer call was placed — `manual`, `preview` or `power` — on the rep's leg as on the
   *  lead's; null on every other call. */
  dial_mode: string | null;

  amd_result: string | null;
  human_answer: boolean;
  abandoned: boolean;
  /**
   * The caller was blocked on this call (ADR-159 in platform): `number`, turned away with a busy
   * tone, or `hidden`, told hidden calls are not taken. The switch turned them away, or a rep
   * blocked them as the call rang. Absent from a core-api that predates it.
   */
  blocked?: "number" | "hidden" | null;
  /**
   * The recording this leg is part of, off the `acl_recording_id` channel variable. One file is
   * captured per call — on the A-leg — and both parties' legs are stamped with its id, so on a
   * bridged call this names the OTHER leg's uuid rather than this one's.
   *
   * Still not the field to test for "is there audio": it says the switch started a recording,
   * not that the file reached the bucket or that this reader may hear it. That is
   * `has_recording`.
   */
  recording_id: string | null;
  /** Whether the signed-in user can play a recording of this leg. Scoped server-side, so it
   *  means "yours to hear" and not merely "it exists" — a play button rendered on this will
   *  never 404. */
  has_recording: boolean;
  /** Whether this call has a stored transcript — live, from a rep's Transcribe toggle, or from
   *  the whole-file pass over its recording. Unlike `has_recording` this is not an entitlement
   *  answer: a transcript belongs to the call rather than to one leg's endpoint, so anyone who
   *  can see this row can read it. A job that has been queued and has not finished is `false` —
   *  the reader wants to know there is nothing to read yet, not that something was ordered. */
  has_transcript: boolean;

  start_time: string | null;
  answer_time: string | null;
  end_time: string | null;
  duration_seconds: number;
  /** Billed seconds. `null` answer_time means this is 0 and the leg was never picked up. */
  billsec_seconds: number;

  hangup_cause: string | null;
  /**
   * The caller's stay in an ACD line, on a leg that waited in one (ADR-025): the newest stay's line,
   * and how it ended — CONNECTED, DROP, ABANDONED, TIMEOUT, REHOMED or REMOVED; after a handoff
   * nobody picked up, the stay a rep took before it. Null on a leg that never waited and while the
   * stay is open; absent from a platform that predates the columns.
   */
  queue_name?: string | null;
  queue_outcome?: string | null;
  /** The conference room the leg was in: `direct-<uuid>` for a call on a rep's own number,
   *  `call-<uuid>` for one the ACD connected. The one field that relates the legs of a call to
   *  each other; null on a leg that was never in one. */
  room?: string | null;
  /** For a leg a rep brought into that room: who asked (their extension) and how — `consult`,
   *  a word first with the caller held, or `add`, straight onto the call. */
  invited_by?: string | null;
  invite_mode?: string | null;
  hold_count: number;
  hold_seconds: number;
  transfer_count: number;
  /**
   * How the leg sounded, as the switch measured it.
   *
   * `audio_in_mos` tops out at **4.5**, not 5 — that is the ceiling for the codecs this switch
   * negotiates, so a meter scaled to 5 draws every healthy call at 90%. Null is not a bad score:
   * a third of the table carried no RTP at all (a leg that never answered, a carrier leg nothing
   * was measured on), and `lib/call-quality.ts` draws those as an absence.
   */
  audio_in_mos: number | null;
  audio_in_quality_percentage: number | null;
  audio_in_jitter_loss_rate: number | null;
  audio_in_jitter_burst_rate: number | null;
  audio_in_packet_count: number | null;
  audio_in_skip_packet_count: number | null;
  audio_out_packet_count: number | null;
  audio_rtcp_packet_count: number | null;
  read_codec: string | null;
  write_codec: string | null;
  read_rate: number | null;
  write_rate: number | null;

  created_at: string;
}

/** How a colleague was brought into a call, as the invite stamped it on their leg. */
export const INVITE_MODE_LABELS: Record<string, string> = {
  consult: "for a word first",
  add: "to join the call",
};

/** "Brought in by 1001 for a word first", or null for a leg nobody brought in. */
export function broughtInBy(cdr: Pick<Cdr, "invited_by" | "invite_mode">): string | null {
  if (!cdr.invited_by) return null;
  const how = cdr.invite_mode ? (INVITE_MODE_LABELS[cdr.invite_mode] ?? cdr.invite_mode) : null;
  return how ? `Brought in by ${cdr.invited_by} ${how}` : `Brought in by ${cdr.invited_by}`;
}

/** Everything else the switch reported about one leg — the support view. */
/** Another leg of the same call. `id` is what opens it. */
export interface CdrOtherLeg {
  id: number;
  channel_uuid: string;
  leg: string | null;
  /** How it was found to be the same call: the per-call conference both were in, a direct bridge,
   *  or one recording covering both. Shown, because a reader deciding whether two rows are really
   *  one call wants to know which fact says so. */
  relation: "room" | "bridge" | "recording";
  caller_number: string | null;
  callee_number: string | null;
  endpoint_user: string | null;
  start_time: string | null;
  billsec_seconds: number;
}

export interface CdrDetail extends Cdr {
  attempt_n: number | null;
  cadence: string | null;
  step_n: number | null;
  step_id: string | null;

  caller_network_ip: string | null;
  bridge_time: string | null;
  last_application: string | null;
  last_application_args: string | null;

  read_codec: string | null;
  write_codec: string | null;
  read_rate: number | null;
  write_rate: number | null;
  audio_in_quality_percentage: number | null;
  audio_in_jitter_loss_rate: number | null;
  audio_in_jitter_burst_rate: number | null;
  audio_in_packet_count: number | null;
  audio_in_skip_packet_count: number | null;
  audio_out_packet_count: number | null;
  audio_rtcp_packet_count: number | null;

  /** Which side put the phone down, as the endpoint module recorded it on this channel:
   *  `recv_bye` / `send_bye` once the call was up, `recv_cancel` / `send_cancel` before it was
   *  answered, `recv_refuse` / `send_refuse` where it was rejected. `recv_*` is the far end of
   *  THIS leg; `send_*` is the switch ending it, which on a two-leg call means the other party
   *  went first. Null where the module recorded neither. */
  hangup_disposition: string | null;
  /** The other legs of this call, newest first, scoped exactly like the row itself. */
  other_legs: CdrOtherLeg[];

  raw_payload: Record<string, unknown> | null;
}

/** Totals for the filter the table is showing — never for the visible page. */
export interface CdrSummary {
  calls: number;
  answered: number;
  /** 0–1, or `null` when there were no calls: a rate over zero is not zero. */
  answer_rate: number | null;
  /** Legs the dialer abandoned under safe harbour. */
  abandoned: number;
  /**
   * Legs whose queue stay ended with the caller hanging up while waiting, and with no rep before the
   * line's maximum wait. A lead the dialer abandoned is counted in `abandoned` only, so the three add
   * up with no leg counted twice. Absent from a platform that predates them.
   */
  queue_abandoned?: number;
  queue_timed_out?: number;
  /** The switch's reading of the channel, not the reader's: an `inbound` channel is one a rep's
   *  own phone dialled in with, which is a call they *placed*. The two counts below are the
   *  reader's own split and are what a personal history shows. */
  inbound: number;
  outbound: number;
  /** How many of these legs the signed-in user placed, and how many they were rung for. Both 0
   *  for an account with no phone. Mirrors `mine_direction`, so a tile and the filter under it
   *  cannot describe different populations. */
  placed_by_me: number;
  received_by_me: number;
  talk_seconds: number;
  avg_talk_seconds: number | null;
  by_hangup_cause: Record<string, number>;
}

/**
 * How a hangup cause should read on screen. Only the ones an operator sees weekly are
 * translated; anything else falls through to the switch's own spelling, which is what they
 * would search for anyway.
 *
 * `tone` is deliberately not "red = bad". `NO_ANSWER` on an outbound dialer is the expected
 * outcome most of the time, and colouring two thirds of a healthy campaign red trains people to
 * ignore the column. Red is reserved for causes that mean something is broken.
 */
export const HANGUP_CAUSES: Record<string, { label: string; tone: "neutral" | "warning" | "danger" }> = {
  NORMAL_CLEARING: { label: "Completed", tone: "neutral" },
  NO_ANSWER: { label: "No answer", tone: "neutral" },
  ORIGINATOR_CANCEL: { label: "Cancelled", tone: "neutral" },
  LOSE_RACE: { label: "Lost race", tone: "neutral" },
  USER_BUSY: { label: "Busy", tone: "warning" },
  CALL_REJECTED: { label: "Rejected", tone: "warning" },
  NO_USER_RESPONSE: { label: "No response", tone: "warning" },
  RECOVERY_ON_TIMER_EXPIRE: { label: "Timed out", tone: "warning" },
  // The cause the ACD hangs up a waiting caller with when it takes them out of a line.
  ALLOTTED_TIMEOUT: { label: "Timed out", tone: "warning" },
  USER_NOT_REGISTERED: { label: "Rep not signed in", tone: "danger" },
  NO_ROUTE_DESTINATION: { label: "No route", tone: "danger" },
  UNALLOCATED_NUMBER: { label: "Unallocated number", tone: "danger" },
  DESTINATION_OUT_OF_ORDER: { label: "Destination down", tone: "danger" },
  NETWORK_OUT_OF_ORDER: { label: "Network down", tone: "danger" },
  SYSTEM_SHUTDOWN: { label: "Switch restarted", tone: "danger" },
};

export function hangupLabel(cause: string | null): string {
  if (!cause) return "—";
  return HANGUP_CAUSES[cause]?.label ?? cause.replaceAll("_", " ").toLowerCase();
}

export function hangupTone(cause: string | null): "neutral" | "warning" | "danger" {
  return cause ? (HANGUP_CAUSES[cause]?.tone ?? "neutral") : "neutral";
}

/**
 * Who the leg was with, from the point of view of this organisation.
 *
 * A CDR names a caller and a callee, but which of those is "the other party" flips with
 * direction — and a table column that sometimes shows the rep and sometimes the customer is a
 * column nobody can scan. Returns both halves so the row can show them in a fixed order.
 */
export function callParties(cdr: Pick<Cdr, "direction" | "caller_number" | "caller_name" | "callee_number" | "callee_name">): {
  from: { number: string; name: string | null };
  to: { number: string; name: string | null };
} {
  return {
    from: { number: cdr.caller_number ?? "—", name: cdr.caller_name },
    to: { number: cdr.callee_number ?? "—", name: cdr.callee_name },
  };
}

// --- reports (ADR-074) ---------------------------------------------------------------------------
//
// Mirrors the `/reports/*` responses in core-api's `schemas.py`. A rate or an average is null over
// an empty denominator; a series lists every bucket, labelled with its start on the report's own
// clock (`2026-09-21T00:00:00-07:00`), which `lib/reports.ts` reads without moving it into the
// browser's zone.

export interface ReportWindow {
  since: string;
  until: string;
  tz: string;
  granularity: "hour" | "day" | "week" | "month";
  weekdays: number[];
  hour_from: number;
  hour_to: number;
  previous_since: string | null;
  previous_until: string | null;
}

export interface LineCounts {
  offered: number;
  answered: number;
  abandoned: number;
  timed_out: number;
  answer_rate: number | null;
  abandon_rate: number | null;
  service_level: number | null;
  wait_avg: number | null;
  talk_total: number;
}

export interface LineSeriesPoint extends LineCounts {
  start: string;
}

export interface LineHeatCell extends LineCounts {
  weekday: number;
  hour: number;
}

export interface WaitBin {
  lo: number;
  hi: number | null;
  answered: number;
  abandoned: number;
  timed_out: number;
}

export interface ReportLineRow extends LineStats {
  campaign_id: string | null;
  campaign_name: string | null;
  purpose: string | null;
  description: string | null;
  /** Gone from the configuration; its history is not. */
  deleted: boolean;
}

export interface SkillRoutingRow extends LineCounts {
  slug: string;
  name: string | null;
  required: number;
  preferred: number;
  relaxed: number;
  answered_by_holder: number;
  answered_by_other: number;
}

export interface RepNumberRow {
  rep: string;
  name: string | null;
  calls: number;
  their_rep: number;
  someone_else: number;
  nobody: number;
  moved_or_open: number;
}

export interface HandoffSummary {
  requested: number;
  connected: number;
  cancelled: number;
  abandoned: number;
  failed: number;
  in_progress: number;
  connect_rate: number | null;
  by_mode: Record<string, number>;
  named_closer: number;
  any_closer: number;
  expired: number;
  wait_avg: number | null;
  wait_p90: number | null;
  by_line: { queue: string; requested: number; connected: number; cancelled: number; abandoned: number; failed: number }[];
}

export interface LineReport {
  window: ReportWindow;
  sla_seconds: number;
  truncated: boolean;
  totals: LineStats;
  previous: LineStats | null;
  lines: ReportLineRow[];
  series: LineSeriesPoint[];
  heatmap: LineHeatCell[];
  waits: WaitBin[];
  abandons: {
    abandoned: number;
    after_a_rep_rang: number;
    before_any_rep_rang: number;
    dropped_as_answered: number;
    timed_out: number;
    wait_avg: number | null;
    wait_p50: number | null;
    wait_p90: number | null;
    by_kind: Record<string, number>;
  };
  skills: SkillRoutingRow[];
  rep_numbers: {
    calls: number;
    their_rep: number;
    someone_else: number;
    nobody: number;
    moved_or_open: number;
    reps: RepNumberRow[];
  };
  handoffs: HandoffSummary;
}

export interface DialCounts {
  attempts: number;
  answered: number;
  contacts: number;
  voicemail: number;
  no_answer: number;
  busy: number;
  failed: number;
  abandoned: number;
  canceled: number;
  other: number;
  in_progress: number;
  counted: number;
  technical_retries: number;
  answer_rate: number | null;
  contact_rate: number | null;
  abandon_rate: number | null;
}

export interface DialingReport {
  window: ReportWindow;
  truncated: boolean;
  totals: DialCounts;
  previous: DialCounts | null;
  series: (DialCounts & { start: string })[];
  heatmap: (DialCounts & { weekday: number; hour: number })[];
  by_mode: (DialCounts & { dial_mode: string; initiated_by: string })[];
  by_campaign: (DialCounts & { campaign_id: string | null; campaign_name: string | null })[];
  cadence_steps: (DialCounts & {
    campaign_id: string | null;
    campaign_name: string | null;
    cadence: string | null;
    cycle: number | null;
    step: number | null;
  })[];
  did_strategies: (DialCounts & { strategy: string })[];
  caller_ids: (DialCounts & { did: string; top_causes: Record<string, number>; low_answer_rate: boolean; last_used: string })[];
  carriers: (DialCounts & { gateways: string[]; label: string; failure_causes: Record<string, number> })[];
  answer_to_rep: { calls: number; avg: number | null; p50: number | null; p90: number | null; max: number | null };
  abandonment: {
    campaign_id: string | null;
    campaign_name: string | null;
    live_answers: number;
    abandoned: number;
    abandon_rate: number | null;
    over_limit: boolean;
  }[];
  abandonment_since: string;
  blocked: Record<string, number>;
  problems: {
    campaign_id: string;
    campaign_name: string | null;
    rep: string;
    rep_name: string | null;
    reason: string;
    first_seen: string;
    last_seen: string;
    count: number;
  }[];
}

export interface OutcomeSummary {
  total: number;
  on_calls: number;
  on_records: number;
  success: number;
  success_rate: number | null;
  calls_served: number;
  dispositioned_rate: number | null;
}

export interface OutcomeReport {
  window: ReportWindow;
  truncated: boolean;
  summary: OutcomeSummary;
  previous: OutcomeSummary | null;
  by_disposition: {
    slug: string;
    label: string;
    category: string | null;
    kind: string | null;
    count: number;
    share: number | null;
    talk_avg: number | null;
  }[];
  by_category: Record<string, number>;
  by_campaign: {
    campaign_id: string | null;
    campaign_name: string | null;
    total: number;
    success: number;
    success_rate: number | null;
    by_category: Record<string, number>;
    by_disposition: Record<string, number>;
  }[];
  series: {
    start: string;
    total: number;
    success: number;
    contact: number;
    no_contact: number;
    compliance: number;
    uncategorized: number;
  }[];
}

export interface ListReport {
  window: ReportWindow;
  as_of: string;
  campaigns: {
    campaign_id: string;
    campaign_name: string | null;
    total: number;
    by_state: Record<string, number>;
    dialable: number;
    due_now: number;
    held: number;
    finished: number;
    blocked: number;
    blocked_reasons: Record<string, number>;
    attempted: number;
    penetration: number | null;
    attempts: { attempts: number; leads: number; or_more: boolean }[];
    added_in_window: number;
    lists: {
      id: number;
      name: string;
      source: string;
      status: string;
      created_at: string;
      imported: number;
      duplicates: number;
      skipped: number;
      by_state: Record<string, number>;
    }[];
  }[];
}

export interface SpeedToLead {
  leads: number;
  dialed: number;
  contacted: number;
  called_in_first: number;
  never_dialed: number;
  first_dial_avg: number | null;
  first_dial_p50: number | null;
  first_dial_p90: number | null;
  first_contact_p50: number | null;
  first_contact_p90: number | null;
  within_5_minutes: number | null;
  bins: { lo: number; hi: number | null; leads: number }[];
}

export interface StageCount {
  stage: string | null;
  group: string;
  count: number;
}

export interface LeadReport {
  window: ReportWindow;
  truncated: boolean;
  speed: SpeedToLead;
  previous: SpeedToLead | null;
  where_now: StageCount[];
  moves: {
    moves: number;
    into: StageCount[];
    reasons: Record<string, number>;
    signed: number;
    days_to_sign_p50: number | null;
    days_to_sign_p90: number | null;
  };
  firms: {
    firm_id: number | null;
    label: string;
    leads: number;
    dialed: number;
    contacted: number;
    contact_rate: number | null;
    working: number;
    scheduled: number;
    signed: number;
    not_qualified: number;
    sign_rate: number | null;
  }[];
  series: { start: string; new: number; signed: number }[];
}

export interface RepReportRow {
  user: string;
  domain: string;
  name: string | null;
  skills: string[];
  calls: number;
  inbound: number;
  outbound: number;
  handoff: number;
  talk_total: number;
  talk_avg: number | null;
  ring_avg: number | null;
  hung_up_by_rep: number;
  hung_up_by_caller: number;
  handed_off: number;
  dropped: number;
  missed_offers: number;
  missed_by_reason: Record<string, number>;
  signed_in_seconds: number;
  first_in: string | null;
  last_out: string | null;
  state_seconds: Record<string, number>;
  break_seconds: Record<string, number>;
  occupancy: number | null;
  after_call_avg: number | null;
  handle_avg: number | null;
  calls_per_hour: number | null;
  outcomes: number;
  success: number;
  success_rate: number | null;
  outcome_rate: number | null;
  outcome_mix: Record<string, number>;
  handoffs_sent: number;
  handoffs_connected: number;
  handoffs_taken: number;
  dials: number;
  dial_contacts: number;
  dialer_calls: number;
  mos_avg: number | null;
  mos_calls: number;
  poor_calls: number;
}

export interface RepTeamSummary {
  reps: number;
  calls: number;
  talk_total: number;
  talk_avg: number | null;
  missed_offers: number;
  signed_in_seconds: number;
  state_seconds: Record<string, number>;
  occupancy: number | null;
  after_call_avg: number | null;
  handle_avg: number | null;
  calls_per_hour: number | null;
  outcomes: number;
  success: number;
  success_rate: number | null;
  handoffs_sent: number;
  handoffs_taken: number;
  dials: number;
  mos_avg: number | null;
  poor_calls: number;
}

export interface RepReport {
  window: ReportWindow;
  truncated: boolean;
  team: RepTeamSummary;
  previous: RepTeamSummary | null;
  reps: RepReportRow[];
  series: { start: string; calls: number; talk_total: number; staffed_seconds: number; break_seconds: number; occupancy: number | null }[];
  heatmap: { weekday: number; hour: number; calls: number; talk_total: number }[];
  timesheet: {
    user: string;
    domain: string;
    name: string | null;
    day: string;
    first_in: string | null;
    last_out: string | null;
    signed_in_seconds: number;
    break_seconds: Record<string, number>;
    calls: number;
    talk_total: number;
  }[];
}

export interface ReportOptions {
  campaigns: { id: string; name: string; purpose: string; status: string; dial_mode: string; firm_id: number | null }[];
  lines: { name: string; description: string | null; campaign_id: string | null; purpose: string | null; enabled: boolean }[];
  reps: { user: string; domain: string; name: string | null; role: string | null; active: boolean; skills: string[] }[];
  skills: { slug: string; name: string; category: string | null }[];
  firms: { id: number; label: string; code: string; status: string }[];
  dispositions: { slug: string; label: string; category: string | null; kind: string | null; active: boolean }[];
}
